import { PreviewPlan, StyleExample } from 'api/video';
import { VideoTemplateStyle } from './types';

/**
 * SYM-6226. What the style step knows about its three examples.
 *
 * THREE STATES AND NO MORE, and that is a consequence of how they are asked
 * for rather than a simplification. The server answers all three looks in one
 * call, on purpose (the three must agree on the picture, or a customer reads
 * the difference between two photographs as part of the look). So there is no
 * state in which one example is here and another is not, and nothing in the
 * step has to decide what two thirds of an answer looks like.
 */
export type StyleExamplesState =
  | { status: 'loading' }
  | {
      status: 'ready';
      examples: StyleExample[];
      /**
       * False when the colour the examples are drawn in is our navy rather
       * than the customer's own. The step says so, and says where to set one.
       */
      brandHasOwnColour: boolean;
      /** Which brand the "set your brand colour" link opens. */
      brandId: number | null;
    }
  | { status: 'failed' };

/**
 * The plan for one look, or nothing.
 *
 * Looked up by name rather than taken by position, although the server always
 * answers dynamic, professional, minimal in that order. The order the cards are
 * drawn in belongs to the cards (TEMPLATE_STYLES), so reading the answer by
 * position would silently pair a card with another look's example the day
 * either list changes -- and three examples that are all real but in the wrong
 * boxes is the one failure a customer cannot see.
 */
export const planForStyle = (
  examples: readonly StyleExample[],
  style: VideoTemplateStyle
): PreviewPlan | null =>
  examples.find((example) => example.style === style)?.plan ?? null;

/**
 * Where one example's clock is, given a clock that never stops.
 *
 * The examples loop, like the clips they replace: the step is comparing looks
 * and a customer must not have to reopen it to see the movement again. One
 * clock drives all three (see useLoopingClock), and each example folds it into
 * its OWN length, because a look's pace is one of the things it decides --
 * three plans of different lengths stay in step with the wall clock rather
 * than with each other.
 */
export const loopedTime = (clock: number, duration: number): number =>
  duration > 0 ? clock % duration : 0;
